<template>
  <div class="ex-checkpoints-status-box">
    <EXTooltip
      v-if="shouldShowTooltip && !myStuffTreeMode"
      placement="bottom"
      :description="tooltipDescription"
      :delay="500"
    >
      <div
          :class="[
          'status-box',
          colorVariant,
          {
            'x-mark': checkpointsValue === null,
            'check-mark': checkpointsValue === 0,
            'number': checkpointsValue && checkpointsValue > 0
          }
        ]"
      >
        <span
            v-if="checkpointsValue === null"
            class="no-checkpoints-text"
        >X</span>
        <span class="d-flex justify-center items-center" v-else-if="checkpointsValue === 0">
          <v-icon
              style="width: 17px"
          >
            $ex-all-checkpoints-passed
          </v-icon>
        </span>
        <span
            v-else-if="checkpointsValue && checkpointsValue > 0"
            class="checkpoints-number-text"
        >{{ checkpointsValue }}</span>
      </div>
    </EXTooltip>
    <div v-else>
      <div
        :class="[
          'status-box',
          colorVariant,
          {
            'x-mark': checkpointsValue === null,
            'check-mark': checkpointsValue === 0,
            'number': checkpointsValue && checkpointsValue > 0
          }
        ]"
      >
        <span
            v-if="checkpointsValue === null"
            class="no-checkpoints-text"
        >X</span>
        <span class="d-flex justify-center items-center" v-else-if="checkpointsValue === 0">
          <v-icon
              style="width: 17px"
          >
            $ex-all-checkpoints-passed
          </v-icon>
        </span>
        <span
            v-else-if="checkpointsValue && checkpointsValue > 0"
            class="checkpoints-number-text"
        >{{ checkpointsValue }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const props = defineProps({
  checkpointsValue: {
    type: [Number, String], // Vue does NOT support `null` in the type array
    required: false, // Make it optional to allow null values
    default: null, // Set null as the default value
    validator: (value) => {
      if (value === null) return true; // Explicitly allow null
      if (value === 0) return true;
      const numValue = Number(value);
      return !isNaN(numValue) && numValue > 0;
    },
  },
  type: {
    type: String,
    default: 'apprentice',
    validator: (value) => ['apprentice', 'professional', 'coach', 'master'].includes(value)
  },
  checkpointLength: {
    type: Number,
    default: 0
  },
  currentBadgeStatus: {
    type: String,
    default: 'null'
  },
  myStuffTreeMode: {
    type: Boolean,
    default: false,
  },
});

const shouldShowTooltip = computed(() =>
  props.checkpointLength > 0 ||
  props.currentBadgeStatus !== 'null'
);

const tooltipDescription = computed(() => {
  if (props.checkpointsValue == null) {
    return `No Active Checkpoints for ${props.type.charAt(0).toUpperCase() + props.type.slice(1)}`
  } else if (props.checkpointsValue === 0) {
    return `All checkpoints passed for ${props.type.charAt(0).toUpperCase() + props.type.slice(1)}`
  } else if (props.checkpointsValue && props.checkpointsValue > 0) {
    return `Remaining checkpoints to be passed for ${props.type.charAt(0).toUpperCase() + props.type.slice(1)}`
  }
})

const colorVariant = computed(() => {
  return props.type;
});
</script>

<style scoped>
.ex-checkpoints-status-box {
  height: 24px;
  width: 24px;
}
.no-checkpoints-text {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
}

.checkpoints-number-text {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
}

.status-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  font-weight: bold;
  font-size: 18px;
  box-sizing: border-box;
}

.status-box.x-mark {
  color: var(--x-color, #000);
}

.status-box.check-mark {
  color: var(--check-color, #00FF19);
}

::v-deep.status-box.professional.check-mark svg path {
  stroke: #00B813 !important;
}

.status-box.apprentice.number {
  color: var(--number-color, #E58700);
}

.status-box.professional.number {
  color: var(--number-color, #00B813);
}

.status-box.coach.number {
  color: var(--number-color, #00A3FF);
}

.status-box.master.number {
  color: var(--number-color, #2E1EC1);
}

.status-box.apprentice {
  border-color: #E58700;
  background-color: #FFEFEF;
}

.status-box.professional {
  border-color: #00B813;
  background-color: #F3FFF9;
}

.status-box.coach {
  border-color: #00A3FF;
  background-color: #EFFEFF;
}

.status-box.master {
  border-color: #2E1EC1;
  background-color: #F2FAFF;
}
</style>